import { div, h1, p, span, button, img, select, option, fnstate } from '@srfnstack/fntags'

// Review what the quality gate decided (mockup + artwork), and 👍/👎 to guide it.
// A 👎 with a note is fed back into the gate prompt as calibration.
export const AutopilotDecisions = () => {
  const decisions = fnstate([])
  const loading = fnstate(true)
  const filter = fnstate('')
  const rankFilter = fnstate('unranked') // unranked | ranked | all — default hides ones you've voted
  const lightbox = fnstate(null)

  const load = async () => {
    loading(true)
    try {
      const q = filter() ? `?verdict=${encodeURIComponent(filter())}` : ''
      const r = await fetch(`/api/system/autopilot/decisions${q}`)
      decisions(r.ok ? await r.json() : [])
    } catch { decisions([]) }
    loading(false)
  }

  const vote = async (item, v) => {
    let note = item.feedback_note || ''
    // MUST be awaited: state.js replaces window.prompt with a modal that returns a
    // Promise. Unawaited, the Promise itself was sent as the note, serialised to
    // an empty object, and stored as the literal string "[object Object]" — which
    // fetchGateCalibration then fed back into the gate prompt as the operator's
    // "lesson". Every note typed since the feature shipped was lost that way.
    if (v === 'down') note = (await window.prompt('Optional — why was this wrong? (a short note helps the gate learn your taste)', note)) ?? note
    try {
      const r = await fetch('/api/system/autopilot/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: item.id, vote: v, note })
      })
      if (r.ok) {
        decisions(decisions().map(d => d.id === item.id ? { ...d, feedback_vote: v, feedback_note: note } : d))
      } else {
        const e = await r.json().catch(() => ({}))
        await window.alert(`Failed: ${e.error || 'error'}`)
      }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
  }

  const verdictClass = (v) => v === 'pass' ? 'badge-success' : v === 'reject' ? 'badge-error' : 'badge-warning'

  const card = (item) => div({ class: 'card bg-base-200 border border-base-300 p-3 flex-row gap-3 items-start' },
    img({
      src: `/api/image?file=${encodeURIComponent(item.file_path)}`,
      class: 'w-24 h-24 object-cover rounded bg-base-100 flex-shrink-0 cursor-zoom-in',
      title: 'Click to view full screen',
      loading: 'lazy',
      decoding: 'async',
      onclick: () => lightbox(`/api/image?file=${encodeURIComponent(item.file_path)}`)
    }),
    div({ class: 'flex-1 min-w-0' },
      div({ class: 'flex flex-wrap items-center gap-2 mb-1' },
        span({ class: 'badge badge-ghost badge-sm' }, item.type),
        span({ class: `badge badge-sm ${verdictClass(item.verdict)}` }, item.verdict || '?'),
        item.score ? span({ class: 'text-xs opacity-70' }, `score ${item.score}`) : '',
        item.rejected ? span({ class: 'badge badge-error badge-xs' }, 'hidden') : '',
        item.salvaged ? span({ class: 'badge badge-info badge-xs' }, 'salvaged') : ''
      ),
      p({ class: 'text-xs opacity-70 mb-2' }, item.reason || '(no reason recorded)'),
      div({ class: 'flex items-center gap-2' },
        button({
          class: `btn btn-xs ${item.feedback_vote === 'up' ? 'btn-success' : 'btn-ghost'}`,
          onclick: () => vote(item, 'up')
        }, '👍 agree'),
        button({
          class: `btn btn-xs ${item.feedback_vote === 'down' ? 'btn-error' : 'btn-ghost'}`,
          onclick: () => vote(item, 'down')
        }, '👎 wrong'),
        item.feedback_note ? span({ class: 'text-xs opacity-60 italic' }, `"${item.feedback_note}"`) : ''
      )
    )
  )

  load()

  return div({ class: 'p-6' },
    h1({ class: 'text-3xl font-bold mb-1' }, 'Autopilot Decisions'),
    p({ class: 'text-sm opacity-70 mb-4' }, 'What the quality gate decided, and your feedback. 👎 with a note teaches the gate your taste — it reads recent feedback back into its prompt.'),
    div({ class: 'mb-4 flex flex-wrap gap-2' },
      select({
        class: 'select select-bordered select-sm',
        onchange: (e) => { rankFilter(e.target.value); decisions(decisions()) }
      },
      option({ value: 'unranked' }, 'Needs ranking'),
      option({ value: 'ranked' }, 'Ranked'),
      option({ value: 'all' }, 'All')),
      select({
        class: 'select select-bordered select-sm',
        onchange: (e) => { filter(e.target.value); load() }
      },
      option({ value: '' }, 'All verdicts'),
      option({ value: 'pass' }, 'Passed'),
      option({ value: 'reroll' }, 'Reroll'),
      option({ value: 'reject' }, 'Rejected'))
    ),
    loading.bindAs(l => l
      ? div({ class: 'flex justify-center p-12' }, span({ class: 'loading loading-spinner loading-lg' }))
      : decisions.bindAs(list => {
        const rf = rankFilter()
        const shown = (Array.isArray(list) ? list : []).filter(d =>
          rf === 'all' ? true : rf === 'ranked' ? !!d.feedback_vote : !d.feedback_vote)
        if (shown.length === 0) {
          const msg = !Array.isArray(list) || list.length === 0
            ? 'No gate decisions yet — they appear once autopilot (or manual generation) runs.'
            : rf === 'unranked'
              ? 'All caught up — nothing left to rank. Switch to "Ranked" or "All" to review.'
              : rf === 'ranked'
                ? "You haven't ranked anything yet."
                : 'Nothing matches this filter.'
          return div({ class: 'text-center opacity-60 p-12' }, msg)
        }
        return div({ class: 'grid gap-3 max-w-3xl' }, shown.map(card))
      })),
    // Full-screen lightbox — click anywhere to close.
    lightbox.bindAs(src => src
      ? div({
        class: 'fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out',
        onclick: () => lightbox(null)
      },
      img({ src, class: 'max-w-full max-h-full object-contain', decoding: 'async' }),
      button({ class: 'btn btn-circle btn-sm absolute top-4 right-4', onclick: () => lightbox(null) }, '✕'))
      : '')
  )
}
